import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, RouterModule} from '@angular/router';
import {isAutoLoginRequest, STAFF_SIGN_IN_ENABLED, upstreamUnavailableMessage, UserLoginData} from '@raptor-fe/core';
import {
    AuthService,
    MsalAuthService,
    NamedRoutes,
    RouterService,
    ToastService,
    TokenService,
    TranslationService,
    URLService,
} from '@raptor-fe/services';
import {SystemAnnouncementsWidgetComponent, WebBaseComponent} from '@raptor-fe/ui';
import {Buffer} from 'buffer';
import {firstValueFrom, takeUntil} from 'rxjs';

type LoginViewState = 'form' | 'loading' | 'error';

/**
 * Query parameter that reveals staff sign-in. Withholding the button is a presentation choice for the
 * overwhelming majority who hold no staff role and would reach a refusal that names no reason; the
 * endpoint is anonymous either way, so this is not an access control.
 */
const STAFF_SIGN_IN_PARAM = 'staff';

@Component({
    selector: 'marlin-web-login',
    imports: [FormsModule, ReactiveFormsModule, RouterModule, SystemAnnouncementsWidgetComponent],
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true,
})
export class LoginComponent extends WebBaseComponent implements OnInit {
    protected formGroup: FormGroup;
    protected viewState: LoginViewState = 'form';
    protected autoLoginErrorMessage = '';
    protected readonly staffSignInEnabled = inject(STAFF_SIGN_IN_ENABLED);
    protected readonly staffSignInRequested = signal(false);
    protected readonly entraPending = signal(false);
    private autoLoginTaken = false;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly authService = inject(AuthService);
    private readonly alertService = inject(ToastService);
    private readonly routerService = inject(RouterService);
    private readonly tokenService = inject(TokenService);
    private readonly urlService = inject(URLService);
    private readonly translationService = inject(TranslationService);
    private readonly msalAuthService = inject(MsalAuthService);
    constructor() {
        super();

        this.formGroup = new FormGroup({
            username: new FormControl('', Validators.required),
            password: new FormControl('', Validators.required),
            base_url: new FormControl(this.urlService.getURL()),
        });

        this.activatedRoute.queryParams.pipe(takeUntil(this.destroySubject)).subscribe(async (params) => {
            if (params[STAFF_SIGN_IN_PARAM] === '1') {
                this.revealStaffSignIn();
            }

            const base_url = params['base_url'];
            const username = params['username'];
            const encodedPassword = params['password'];
            const branchId = params['branchId'];
            const isAutoLogin = isAutoLoginRequest(params);

            if (this.authService.isLoggedIn() && !isAutoLogin) {
                this.routerService.navigateAndReplaceUrl(NamedRoutes.landing());
                return;
            }

            if (this.authService.isLoggedIn() && isAutoLogin) {
                this.tokenService.removeToken();
                this.tokenService.removeRefreshToken();
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
            }

            const password = encodedPassword ? Buffer.from(encodedPassword, 'base64').toString('utf-8') : undefined;
            const encodedRedirectRoute = params['redirectRoute'];
            const redirectRoute = encodedRedirectRoute ? Buffer.from(encodedRedirectRoute, 'base64').toString('utf-8') : '';
            const webview = params['webview'];
            if (webview === 'true') {
                this.applicationContextStore.setIsWebViewMode(true);
            }

            if (base_url && username && password) {
                this.formGroup.patchValue({
                    base_url: base_url,
                    username: username,
                    password: password,
                });

                this.viewState = 'loading';
                this.autoLoginTaken = true;
                this.onSubmit(redirectRoute, true, branchId);
            }
        });

    }

    ngOnInit(): void {
        if (this.autoLoginTaken) {
            return;
        }

        void this.collectStaffSignIn();
    }

    private async collectStaffSignIn(): Promise<void> {
        try {
            const staffToken = await this.msalAuthService.completeSignIn();
            if (staffToken) {
                this.revealStaffSignIn();
                await this.exchangeStaffToken(staffToken);
            }
        } catch (error) {
            this.revealStaffSignIn();
            console.error('Microsoft sign-in did not complete.', error);
            this.alertService.showError('Microsoft sign-in could not be completed. Please try again.');
        }
    }

    /**
     * Latches on rather than tracking the parameter, because the return leg can land on the home
     * page without it when the bridge cannot recover the initiating url. Without latching, a staff
     * member whose exchange fails lands back on a form offering no way to retry.
     */
    private revealStaffSignIn(): void {
        this.staffSignInRequested.set(true);
    }

    protected async signInWithEntra(): Promise<void> {
        this.entraPending.set(true);
        try {
            await this.msalAuthService.signIn();
        } catch (error) {
            this.entraPending.set(false);
            console.error('Could not start Microsoft sign-in.', error);
            this.alertService.showError(
                this.msalAuthService.isConfigured()
                    ? 'Could not start Microsoft sign-in. Close this tab and try again.'
                    : 'Microsoft sign-in is not configured for this environment.',
            );
        }
    }

    /**
     * Runs on the return leg. A 401 carries no reason by design, but a 503 means the directory was
     * unreachable, which is retryable and must not be reported as an authorisation refusal.
     */
    private async exchangeStaffToken(staffToken: string): Promise<void> {
        this.viewState = 'loading';
        try {
            await firstValueFrom(this.authService.staffLogin(staffToken));
            await firstValueFrom(this.translationService.getDictionary()).catch(() => {
                this.alertService.showError('Error fetching translation dictionary for selected language.');
            });
            this.routerService.navigateAndReplaceUrl(NamedRoutes.welcome());
        } catch (error) {
            this.viewState = 'form';
            console.error('Staff token exchange failed.', error);
            this.alertService.showError(this.staffLoginFailureMessage(error));
        }
    }

    private staffLoginFailureMessage(error: unknown): string {
        const status = error instanceof HttpErrorResponse ? error.status : undefined;

        if (status === 401) {
            return 'Your Microsoft account is not authorised for Raptor staff access.';
        }

        if (status === 503) {
            return 'Microsoft sign-in is temporarily unavailable. Please try again shortly.';
        }

        return 'Microsoft sign-in could not be completed. Please try again or contact support.';
    }

    async onSubmit(redirectRoute = '', isAutoLogin = false, branchId?: string): Promise<void> {
        if (this.formGroup.valid) {
            const formData = this.formGroup.value;

            const userLoginData: UserLoginData = {
                username: formData.username,
                password: formData.password,
            };

            this.tokenService.removeToken();
            this.tokenService.removeRefreshToken();
            this.urlService.saveURL(formData.base_url);

            await firstValueFrom(this.authService.login(userLoginData, formData.base_url)).then(
                async () => {
                    await firstValueFrom(this.translationService.getDictionary()).catch(() => {
                        this.alertService.showError('Error fetching translation dictionary for selected language.');
                    });
                    this.routerService.navigateAndReplaceUrl(NamedRoutes.welcome(redirectRoute, branchId));
                },
                (result) => {
                    const message =
                        upstreamUnavailableMessage(result, this.applicationContextStore.dictionary()) ?? result?.error?.message ?? 'Login failed.';
                    if (isAutoLogin) {
                        this.autoLoginErrorMessage = message;
                        this.viewState = 'error';
                    } else {
                        this.alertService.showError(message);
                    }
                },
            );
        }
    }
}
